import { toast } from "@/components/ui/use-toast";
import { MESSAGES } from "../../utils/messages";
import {
  dropResolvedCampaigns,
  removableCampaignsOf,
  type CampaignConflictItem,
  type PendingRemoval,
} from "../../utils/conflict-resolution";

interface UseConflictResolutionParams {
  conflicts: CampaignConflictItem[];
  /** Recebe sempre a lista final (valor, nunca updater) para facilitar o sync com o form. */
  setConflicts: (next: CampaignConflictItem[]) => void;
  /** Remove o SKU dos itens/listas locais da nova campanha (ação "Descartar"). */
  discardItemLocally: (itemCode: string) => void;
  /** Estagia a remoção das campanhas anteriores (aplicada só ao criar a campanha). */
  stageRemoval: (removal: PendingRemoval) => void;
  /** Chamado quando não resta mais nenhum conflito (ex.: fecha o modal). */
  onAllResolved: () => void;
}

interface UseConflictResolutionReturn {
  handleRemoveFromPrevious: (conflict: CampaignConflictItem) => void;
  handleDiscardItem: (itemCode: string) => void;
}

/**
 * Resolução in-place de conflitos — SEM efeito colateral imediato no backend.
 * - "Remover da campanha anterior": ESTAGIA a remoção (mantém o SKU na nova
 *   campanha); a remoção real só acontece ao criar a campanha (payload
 *   `conflictResolutions`), evitando alterar a campanha anterior à toa.
 * - "Descartar item": remove o SKU apenas localmente.
 *
 * Compartilhado pelos forms (StepFour) e pelo modal de conflito.
 */
export function useConflictResolution({
  conflicts,
  setConflicts,
  discardItemLocally,
  stageRemoval,
  onAllResolved,
}: UseConflictResolutionParams): UseConflictResolutionReturn {
  const handleRemoveFromPrevious = (conflict: CampaignConflictItem) => {
    const removable = removableCampaignsOf(conflict);
    if (removable.length === 0) return; // só PRO013 → nada a estagiar (botão fica desabilitado)

    stageRemoval({
      itemCode: conflict.itemCode,
      itemName: conflict.itemName,
      conflictingCampaigns: removable,
    });

    // Tira as campanhas estagiadas do conflito; itens sem conflito restante saem da lista.
    const remaining = dropResolvedCampaigns(
      conflicts,
      conflict.itemCode,
      removable,
    ).filter((c) => c.conflictingCampaigns.length > 0);

    setConflicts(remaining);
    toast(MESSAGES.RESOLVE_CONFLICT.SUCCESS);
    if (remaining.length === 0) onAllResolved();
  };

  const handleDiscardItem = (itemCode: string) => {
    discardItemLocally(itemCode);
    const remaining = conflicts.filter((c) => c.itemCode !== itemCode);
    setConflicts(remaining);
    toast(MESSAGES.RESOLVE_CONFLICT.INFO!);
    if (remaining.length === 0) onAllResolved();
  };

  return { handleRemoveFromPrevious, handleDiscardItem };
}
